/**
 * Save fixtures (GDD §4.15 union list of 19 ids; architecture §8.1 `loadFixture`): `src/debug/fixtures/<id>.json`,
 * loaded lazily (one chunk each) by `?fixture=<id>` and `__marg.loadFixture(id)` into the in-memory store.
 *
 * A fixture is a PARTIAL save: missing slices are created fresh by their owners' `SliceDef.create`, older slice
 * versions migrate, invalid slices are re-created (the app runs the same `normalizeState` as the load pipeline). The
 * F1 files are skeletons: each slice owner fills its slice when its feature lands (see `notes` in each file).
 */
import { FIXTURE_IDS, type FixtureId, type SliceKey } from '../shared';

export interface FixtureFile {
  id: FixtureId;
  /** One line: what the fixture represents (GDD §4.15, ui-ux §22.1, §5.6). */
  title: string;
  /** Owner notes: which slices must carry what once the features land. */
  notes?: string;
  /** Optional fixed wall clock (ISO) applied with the fixture (`wall=`), e.g. for daily resets. */
  wall?: string | null;
  /** Optional simulated absence (`away=`), e.g. '8h'. */
  away?: string | null;
  /** The partial save. `seed` defaults to `fixture:<id>`. */
  save: { seed?: string; createdAt?: number; lastActiveAt?: number; slices?: Partial<Record<SliceKey, Record<string, unknown>>> };
}

const FILES = import.meta.glob('./fixtures/*.json') as Record<string, () => Promise<{ default: FixtureFile }>>;

/** Every fixture id that has a JSON file. */
export function fixtureIds(): FixtureId[] {
  return FIXTURE_IDS.filter((id) => `./fixtures/${id}.json` in FILES);
}

/** Loads a fixture file (null when the id has no file). */
export async function loadFixtureFile(id: string): Promise<FixtureFile | null> {
  const load = FILES[`./fixtures/${id}.json`];
  if (!load) return null;
  const mod = await load();
  return mod.default;
}
